import { createTheme } from "@mui/material/styles";

interface CustomShape {
  borderRadius: number;
  customRadius: {
    small: number;
    medium: number;
    large: number;
  };
}

export const theme = (mode: "light" | "dark") =>
  createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1024,
        xl: 1440,
      },
    },

    spacing: 2,

    shape: {
      borderRadius: 8,
      customRadius: {
        small: 2,
        medium: 16,
        large: 40,
      },
    } as CustomShape,

    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            userSelect: "none",
          },
        },
      },
    },

    typography: {
      fontFamily: '"IRANYekan", sans-serif ,Arial',
      display1: {
        fontSize: "24px",
        lineHeight: "40px",
        letterSpacing: "0",
      },
      display2: {
        fontSize: "32px",
        lineHeight: "56px",
        letterSpacing: "0",
      },
      h1: {
        fontSize: "20px",
        lineHeight: "32px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "24px",
        },
      },
      h2: {
        fontSize: "18px",
        lineHeight: "28px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "20px",
        },
      },
      h3: {
        fontSize: "16px",
        lineHeight: "25px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "18px",
        },
      },
      h4: {
        fontSize: "14px",
        lineHeight: "23px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "16px",
        },
      },
      caption: {
        fontSize: "12px",
        lineHeight: "18px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "14px",
        },
      },
      overline: {
        fontSize: "10px",
        lineHeight: "12px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "12px",
        },
      },
      overlineMini: {
        fontSize: "8px",
        lineHeight: "10px",
        letterSpacing: "0",
      },
      body1: {
        fontSize: "16px",
        lineHeight: "22px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "18px",
        },
      },
      body2: {
        fontSize: "14px",
        lineHeight: "24px",
        letterSpacing: "0",
        "@media (min-width:600px)": {
          fontSize: "16px",
        },
      },
    },

    palette: {
      mode,
      ...(mode === "light"
        ? {
            text: {
              primary: "#111213",
              secondary: "#ffffff",
              primaryOnBrand: "#111213",
              secondaryOnBrand: "#ffffff",
              primarySolid: "#2f3237",
              disabled: "#8f96a3",
              placeholder: "#8f96a3",
              brandSolid: "#92e82a",
              brandPrimarySolid: "#2e5201",
              infoPrimary: "#0EA5E9",
            },
            background: {
              primary: "#ffffff",
              secondary: "#111213",
              tertiary: "#f6f6f6",
              brandSolid: "#92e82a",
              brandSolidLight: "#f5ffd9",
              brandSolidSecondary: "#fa114f",
              disabled: "#c2c9d6",
              brandSolidSecondaryLighter: "#ffdee1",
              skyPrimary: "#0EA5E9",
              amberPrimary: "#F59E0B",
              skyLight: "#e1f4fd",
            },
            forground: {
              primary: "#92e82a",
              quinary: "#8F96A3",
              senary: "#c2c9d6",
            },
            border: {
              primary: "#8f96a3",
              secondary: "#dee3ed",
              primarySolid: "#3f7902",
              primaryHover: "#c2c9d6",
              primarySubtle: "#111213",
            },
          }
        : {
            text: {
              primary: "#FBF7FD",
              secondary: "#111213",
              primaryOnBrand: "#111213",
              secondaryOnBrand: "#ffffff",
              primarySolid: "#F8F9FC",
              disabled: "#D8D8D8",
              placeholder: "#F5F5F5",
              brandSolid: "#92e82a",
              brandPrimarySolid: "#2E5201",
              infoPrimary: "#0EA5E9",
            },

            background: {
              primary: "#111213",
              secondary: "#ffffff",
              tertiary: "#1d1e20",
              brandSolid: "#92e82a",
              brandSolidLight: "#3F7902",
              brandSolidSecondary: "#fa114f",
              disabled: "#c2c9d6",
              brandSolidSecondaryLighter: "#ffdee1",
              skyPrimary: "#0EA5E9",
              amberPrimary: "#F59E0B",
              skyLight: "#e1f4fd",
            },
            forground: {
              primary: "#92e82a",
              quinary: "#8F96A3",
              senary: "#c2c9d6",
            },
            border: {
              primary: "#dee3ed",
              secondary: "#8f96a3",
              primarySolid: "#3f7902",
              primaryHover: "#c2c9d6",
              primarySubtle: "#ffffff",
            },
          }),
      error: {
        light: "#FEF2F2",
        main: "#EF4444",
        dark: "#7F1D1D",
      },
      warning: {
        light: "#FFFBEB",
        main: "#F59E0B",
        dark: "#78350F",
      },
      success: {
        light: "#F5FFD9",
        main: "#92E82A",
        dark: "#2E5201",
      },
      info: {
        light: "#F2FFFF",
        main: "#1EEAEF",
        dark: "#00494B",
      },
    },
  });
